import { useCallback } from 'react';

import { useLocalStorage } from 'usehooks-ts';

import { useUserUuid } from './useUserUuid';
import { whenTruthy } from '../utils/async';
import { inIframe } from '../utils/window';

export enum Events {
    readyToReceive = 'custom.transmission.readyToReceive',
    send = 'custom.transmission.send'
}

/**
 * If page within an iframe, open a URL and transmit data to it via events.
 * @param {URL} url
 * @param {Record<string, any>} data
 * @param {string} urlTarget
 * @param {boolean} toStorage - if true, the receiver will auto store the
 * contents of data to local storage.
 */
export const useTransmission = () => {
    const transmit = useCallback((url: URL, data: Record<string, any>, urlTarget = '_blank', toStorage = true) => {
        if (inIframe() && !url.searchParams.has('transmission')) {
            url.searchParams.append('transmission', 'true');
        }
        const receivingWindow = window.open(url, urlTarget) as Window;

        const handleReadyToReceive = () => {
            receivingWindow.dispatchEvent(
                new CustomEvent(Events.send, {
                    detail: {
                        data,
                        toStorage
                    }
                })
            );
        };
        if (inIframe()) {
            receivingWindow.addEventListener(Events.readyToReceive, handleReadyToReceive, { once: true });
        }
        return () => {
            if (inIframe()) {
                window.removeEventListener(Events.readyToReceive, handleReadyToReceive);
            }
        };
    }, []);

    const receive = async () => {
        if (!new URLSearchParams(window.location.search).get('transmission')) {
            return;
        }

        // First thing we need to do is remove the transmission param from the URL.
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('transmission');
        window.history.replaceState({}, document.title, newUrl);

        // Listen to this event which is triggered by the iframe page - when this is triggered we are being sent the
        // data from the iframe.
        let receivedTransmission;
        let receivedData;
        window.addEventListener(
            'custom.transmission.send',
            (e) => {
                ({ detail: receivedTransmission } = e as CustomEvent);
                ({ data: receivedData } = receivedTransmission);
            },
            { once: true }
        );

        // Trigger ready event and wait for data.
        // The transmitting page subscribes to this event and will know it's OK to send over its data.
        window.dispatchEvent(new Event('custom.transmission.readyToReceive'));

        // Await receipt of data.
        await whenTruthy(() => receivedTransmission);

        if (receivedTransmission?.toStorage && receivedData) {
            // eslint-disable-next-line no-restricted-syntax
            for (const property of Object.keys(receivedData)) {
                if (Object.prototype.hasOwnProperty.call(receivedData, property)) {
                    window.localStorage.setItem(property, receivedData[property]);
                }
            }
        }

        return receivedData;
    };

    return { transmit, receive };
};

export const useTransmitUserDetails = () => {
    const transmission = useTransmission();
    const userUuid = useUserUuid(null) || '';
    const [userUuidTime] = useLocalStorage('user_uuid_time', true);
    const transmitUserDetails = useCallback(
        (url: URL, urlTarget = '_blank', toStorage = true) => {
            const data = {
                user_uuid: userUuid,
                user_uuid_time: userUuidTime
            };
            transmission.transmit(url, data, urlTarget, toStorage);
        },
        [transmission, userUuid, userUuidTime]
    );
    return { transmitUserDetails };
};
